import { Link, useNavigate } from 'react-router-dom';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { FileText, ChevronRight } from 'lucide-react';

export function MentionsLegalesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MarketingHeader
        onNavigate={(path) => navigate(path)}
        onSignIn={() => navigate('/login')}
        onSignUp={() => navigate('/login')}
      />

      <main className="flex-1">
        <div className="bg-gradient-to-b from-slate-50 to-white border-b border-gray-100 py-12 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-blue-600 transition-colors">Accueil</Link>
              <ChevronRight size={14} />
              <span className="text-gray-800 font-medium">Mentions légales</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-blue-600" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Mentions légales
              </h1>
            </div>
            <p className="text-gray-500 text-sm mt-2">
              Dernière mise à jour : 09/03/2026
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="space-y-10 text-gray-700 leading-relaxed">

            <p>
              Conformément aux dispositions de la Loi pour la confiance dans l'économie numérique, il est précisé aux utilisateurs du site TrezoOne l'identité des différents intervenants dans le cadre de sa réalisation et de son suivi.
            </p>

            <Section title="1. Éditeur du site">
              <p>Le présent site est édité par :</p>
              <div className="mt-4 p-5 bg-gray-50 rounded-xl border border-gray-200 text-sm space-y-2">
                <p className="font-semibold text-gray-900">One System – SASU</p>
                <p className="text-gray-600">Société par actions simplifiée unipersonnelle</p>
                <div className="pt-2 border-t border-gray-200 space-y-1.5">
                  <Row label="Capital social" value="500 €" />
                  <Row label="Siège social" value="1484 Route des Gabloteaux, 16130 Juillac-le-Coq" />
                  <Row label="RCS" value="Angoulême" />
                  <Row label="SIREN" value="101 790 491" />
                </div>
                <div className="pt-2 border-t border-gray-200 space-y-1.5">
                  <Row label="Directeur de la publication" value="Maxime Gacher" />
                  <div className="flex flex-wrap gap-1">
                    <span className="text-gray-500 min-w-[140px]">Contact</span>
                    <a href="mailto:support@trezoone.fr" className="text-blue-600 hover:underline">
                      support@trezoone.fr
                    </a>
                  </div>
                </div>
              </div>
            </Section>

            <Section title="2. Hébergement">
              <p>Le site TrezoOne est hébergé sur une infrastructure cloud exploitée par :</p>
              <div className="mt-4 p-5 bg-gray-50 rounded-xl border border-gray-200 text-sm space-y-1">
                <p className="font-semibold text-gray-900">Amazon Web Services</p>
                <p className="text-gray-600">Amazon Web Services EMEA SARL</p>
                <p className="text-gray-600">38 Avenue John F. Kennedy</p>
                <p className="text-gray-600">L-1855 Luxembourg</p>
              </div>
              <p className="mt-4">Les données sont hébergées dans l'Union européenne, dans la région West EU (Irlande).</p>
            </Section>

            <Section title="3. Activité">
              <p>TrezoOne est une solution logicielle en ligne (SaaS) permettant aux associations, écoles et organisations de gérer et anticiper leur trésorerie.</p>
              <p className="mt-3">Le service est accessible via le site :</p>
              <div className="mt-2">
                <a href="https://trezoone.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                  trezoone.fr
                </a>
              </div>
            </Section>

            <Section title="4. Propriété intellectuelle">
              <p>L'ensemble du contenu présent sur le site TrezoOne, incluant notamment :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>textes</li>
                <li>graphismes</li>
                <li>logos</li>
                <li>icônes</li>
                <li>images</li>
                <li>structure du site</li>
                <li>code source</li>
              </ul>
              <p className="mt-4">est la propriété exclusive de One System, sauf mentions contraires.</p>
              <p className="mt-3">Toute reproduction, représentation, modification, publication ou adaptation de tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite sans autorisation préalable.</p>
            </Section>

            <Section title="5. Responsabilité">
              <p>One System s'efforce de fournir sur le site TrezoOne des informations aussi précises que possible.</p>
              <p className="mt-3">Toutefois, l'éditeur ne pourra être tenu responsable :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-2">
                <li>des omissions</li>
                <li>des inexactitudes</li>
                <li>des carences dans la mise à jour</li>
              </ul>
              <p className="mt-3">Les informations présentes sur le site sont données à titre indicatif.</p>
              <p className="mt-3">L'utilisation de la plateforme TrezoOne ne dispense pas les utilisateurs de leurs obligations comptables ou financières.</p>
            </Section>

            <Section title="6. Accès au service">
              <p>Le site est accessible en permanence, sauf interruption programmée pour maintenance ou cas de force majeure.</p>
              <p className="mt-3">One System ne saurait être tenu responsable d'une indisponibilité temporaire du service.</p>
            </Section>

            <Section title="7. Protection des données personnelles">
              <p>Le traitement des données personnelles est régi par la politique de confidentialité accessible à l'adresse suivante :</p>
              <div className="mt-2">
                <Link to="/politique-confidentialite" className="text-blue-600 hover:underline font-medium">
                  /politique-confidentialite
                </Link>
              </div>
              <p className="mt-4">Les traitements sont réalisés conformément au Règlement général sur la protection des données.</p>
              <p className="mt-3">Les utilisateurs disposent d'un droit d'accès, de rectification et de suppression de leurs données.</p>
              <p className="mt-3">Toute demande peut être adressée à :</p>
              <div className="mt-2">
                <a href="mailto:support@trezoone.fr" className="text-blue-600 hover:underline font-medium">
                  support@trezoone.fr
                </a>
              </div>
              <p className="mt-4">Les utilisateurs peuvent également saisir l'autorité de contrôle compétente, notamment la{' '}
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  CNIL
                </a>.
              </p>
            </Section>

            <Section title="8. Cookies">
              <p>Le site peut utiliser des cookies nécessaires à son fonctionnement.</p>
              <p className="mt-3">Les utilisateurs peuvent accepter, refuser ou personnaliser l'utilisation de certains cookies via le bandeau prévu à cet effet.</p>
            </Section>

            <Section title="9. Droit applicable">
              <p>Les présentes mentions légales sont régies par le droit français.</p>
              <p className="mt-3">En cas de litige, et après tentative de résolution amiable, les tribunaux français seront seuls compétents.</p>
            </Section>

          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-1">
      <span className="text-gray-500 min-w-[140px]">{label}</span>
      <span className="text-gray-800 font-medium">{value}</span>
    </div>
  );
}
